import { axios } from '@argon/app/axios'
import { toFilterString, stampItem } from '@argon/app/helpers'

const NAMESPACE = 'organizations'

const state = {
  fields: [{ label: 'Display Name', name: 'display' }],
  settings: {},
  organizations: [],
  tableItemsCount: null,
  tablePagination: {},
  activeOrg: null,
  activeOrgRefname: null,
  currentOrg: null,
  roles: [],
  users: [],
  usersCount: null,
  invitations: [],
  pendingInvitations: [],
  multiOrg: process.env.VUE_APP_ARGON_MULTI_ORG ? process.env.VUE_APP_ARGON_MULTI_ORG === 'true' : false,
  defaultOrgId: process.env.VUE_APP_ARGON_DEFAULT_ORG_ID ? parseInt(process.env.VUE_APP_ARGON_DEFAULT_ORG_ID) : null
}

const ORG_USERS = 'orgUsers'

const mutations = {
  SET_ORGANIZATIONS: (state, payload) => {
    state.organizations = payload
  },
  SET_TABLE_PAGINATION: (state, payload) => {
    state.tablePagination = payload
  },
  SET_TABLE_ITEMS: (state, payload) => {
    state.tableItems = payload
  },
  SET_TABLE_ITEMS_COUNT: (state, payload) => {
    state.tableItemsCount = payload
  },
  SET_CURRENT_ORGANIZATION: (state, payload) => {
    state.currentOrg = payload
  },
  SET_ACTIVE_ORGANIZATION: (state, payload) => {
    state.activeOrg = payload
  },
  SET_ACTIVE_ORGANIZATION_REFNAME: (state, payload) => {
    state.activeOrgRefname = payload
  },
  SET_USERS: (state, payload) => {
    state.users = payload
  },
  SET_USERS_COUNT: (state, payload) => {
    state.usersCount = payload
  },
  SET_INVITATIONS: (state, payload) => {
    state.invitations = payload
  },
  SET_PENDING_INVITATIONS: (state, payload) => {
    state.pendingInvitations = payload
  },
  SET_SETTINGS: (state, payload) => {
    state.settings = payload || {}
  }
}

const actions = {
  listOrganizations: ({ commit, rootState }, { pager }) => {
    const filters = toFilterString(rootState.app.appliedFilters[NAMESPACE])
    let pagerParams = pager || state.tablePagination
    let params = { filters, ...pagerParams }
    return new Promise((resolve, reject) => {
      axios({
        url: '/organizations',
        method: 'GET',
        params
      })
        .then(({ data }) => {
          commit('SET_ORGANIZATIONS', data.items)
          commit('SET_TABLE_ITEMS_COUNT', data.count)
          commit('SET_TABLE_PAGINATION', pagerParams)
          resolve(data.items)
        })
        .catch(reject)
    })
  },
  clearOrganizations: ({ commit }) => {
    return new Promise((resolve, reject) => {
      commit('SET_TABLE_ITEMS_COUNT', null)
      commit('SET_ORGANIZATIONS', [])
      resolve(true)
    })
  },
  setDefaultOrganization: ({ state, commit, rootGetters }) => {
    return new Promise((resolve, reject) => {
      if (state.defaultOrgId && !state.activeOrg) {
        axios.defaults.headers.common['X-Argon-Organization'] = state.defaultOrgId
        let availableOrganizations = rootGetters['me/organizations']
        let match = availableOrganizations.find((item) => item.id === state.defaultOrgId)
        if (match) {
          commit('SET_ACTIVE_ORGANIZATION', match)
          commit('SET_ACTIVE_ORGANIZATION_REFNAME', match.refname)
        }
        resolve(true)
      }
    })
  },
  setActiveOrganization: ({ commit, dispatch }, match) => {
    return new Promise((resolve, reject) => {
      commit('SET_ACTIVE_ORGANIZATION', match)
      commit('SET_ACTIVE_ORGANIZATION_REFNAME', match.refname)
      axios.defaults.headers.common['X-Argon-Organization'] = match.id
      dispatch('dumpActiveOrganization')
      // We need this to get the updated list of policies for this organization
      dispatch('me/getProfile', {}, { root: true })
      dispatch('app/setContextObj', { ...match, type: 'org' }, { root: true })
      return resolve(match)
    })
  },
  setActiveOrgFromRef: ({ commit, dispatch, state, getters }, payload) => {
    const { orgRef } = payload
    return new Promise((resolve, reject) => {
      let match = getters['getActiveOrg'](orgRef)
      if (!match) {
        return reject(new Error('No matching organization'))
      }
      // if (state.activeOrgRefname && orgRef === state.activeOrgRefname) {
      //   dispatch('app/setContextObj', { ...match, type: 'org' }, { root: true })
      //   return resolve(match)
      // }
      dispatch('setActiveOrganization', match).then(resolve)
    })
  },
  dumpActiveOrganization: ({ state }) => {
    return new Promise((resolve, reject) => {
      localStorage.setItem('_argon_organization', state.activeOrgRefname)
      resolve(true)
    })
  },
  loadActiveOrganization: ({ commit, dispatch }) => {
    return new Promise((resolve, reject) => {
      let orgRef = localStorage.getItem('_argon_organization')
      if (orgRef) {
        dispatch('setActiveOrgFromRef', { orgRef }).catch(() => {})
      }
      resolve(true)
    })
  },
  clearActiveOrganization: ({ commit }) => {
    return new Promise((resolve, reject) => {
      commit('SET_ACTIVE_ORGANIZATION', null)
      commit('SET_ACTIVE_ORGANIZATION_REFNAME', null)
      delete axios.defaults.headers.common['X-Argon-Organization']
      localStorage.removeItem('_argon_organization')
      resolve(true)
    })
  },
  getOrganization: ({ commit, state }, orgId) => {
    return new Promise((resolve, reject) => {
      axios
        .get(`/organizations/${orgId}`)
        .then(({ data }) => {
          commit('SET_CURRENT_ORGANIZATION', data)
          commit('SET_SETTINGS', data.settings || state.settings)
          resolve(data)
        })
        .catch(reject)
    })
  },
  patchOrganization: ({ commit, dispatch }, payload) => {
    const { orgId, data } = payload
    return new Promise((resolve, reject) => {
      axios({
        url: `/organizations/${orgId}`,
        data,
        method: 'PATCH'
      })
        .then(({ data }) => {
          resolve(data)
        })
        .catch(reject)
    })
  },
  patchOrgInContext: ({ commit, dispatch }, payload) => {
    const { orgId, data } = payload
    return new Promise((resolve, reject) => {
      axios({
        url: `/organizations/${orgId}`,
        data,
        method: 'PATCH'
      })
        .then(({ data }) => {
          // Make sure we update our profile before returning
          Promise.all([
            dispatch('me/updateOrganizations', data, { root: true }),
            dispatch('app/setContextObj', { ...data, type: 'org' }, { root: true })
          ]).then(() => {
            commit('SET_CURRENT_ORGANIZATION', data)
            resolve(data)
          })
        })
        .catch(reject)
    })
  },
  deleteOrganization: ({ dispatch }, orgId) => {
    return new Promise((resolve, reject) => {
      axios({ url: `/organizations/${orgId}`, method: 'DELETE' })
        .then(() => {
          return Promise.all([dispatch('me/getProfile', {}, { root: true }), dispatch('listOrganizations', {})])
        })
        .then(resolve)
        .catch(reject)
    })
  },
  clearOrganization: ({ commit, dispatch }) => {
    return new Promise((resolve, reject) => {
      commit('SET_CURRENT_ORGANIZATION', null)
      commit('SET_SETTINGS', {})
      resolve(true)
    })
  },
  createOrganization: ({ dispatch }, payload) => {
    return new Promise((resolve, reject) => {
      axios({
        url: '/organizations',
        data: payload,
        method: 'POST'
      })
        .then(({ data }) => {
          return Promise.all([dispatch('me/getProfile', {}, { root: true }), dispatch('listOrganizations', {})]).then(
            () => {
              return dispatch('setActiveOrganization', data)
            }
          )
        })
        .then(resolve)
        .catch(reject)
    })
  },
  patchSettings: ({ commit, dispatch }, payload) => {
    const { data, orgId } = payload
    return new Promise((resolve, reject) => {
      axios({
        url: `/organizations/${orgId}/settings`,
        data,
        method: 'PATCH'
      })
        .then(({ data }) => {
          dispatch('getOrganization', orgId)
          resolve(data)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  fetchCurrentOrgUsers: ({ state }, { pager, filters }) => {
    let orgId = state.activeOrg ? state.activeOrg.id : null
    return new Promise((resolve, reject) => {
      if (orgId) {
        axios({
          url: `/organizations/${orgId}/users`,
          params: { include: 'user,account', filters, ...pager },
          method: 'GET'
        })
          .then(({ data }) => {
            resolve(data)
          })
          .catch(reject)
      } else {
        reject(new Error('no active organization'))
      }
    })
  },
  listCurrentOrgUsers: ({ dispatch, commit, rootState }, { pager }) => {
    const filters = toFilterString(rootState.app.appliedFilters[ORG_USERS])
    return new Promise((resolve, reject) => {
      dispatch('fetchCurrentOrgUsers', { pager, filters })
        .then(({ items, count }) => {
          commit('SET_USERS', items)
          commit('SET_USERS_COUNT', count)
          resolve(items)
        })
        .catch(reject)
    })
  },
  clearCurrentOrgUsers: ({ commit, dispatch }) => {
    return new Promise((resolve, reject) => {
      commit('SET_USERS', [])
      resolve(true)
    })
  },
  inviteUser: ({ dispatch }, data) => {
    let url = '/invitations'
    return new Promise((resolve, reject) => {
      axios({
        url,
        data,
        method: 'POST'
      })
        .then(({ data }) => {
          dispatch('listInvitations')
          resolve(data)
        })
        .catch(reject)
    })
  },
  updateOrganizationUser: ({ dispatch }, payload) => {
    const { principalIds, attribution, orgId, pager } = payload
    let promises = principalIds.map((userId) =>
      axios({
        url: `/organizations/${orgId}/users/${userId}`,
        data: { attribution },
        method: 'PATCH'
      })
    )
    return new Promise((resolve, reject) => {
      Promise.all(promises)
        .then(() => {
          return dispatch('listCurrentOrgUsers', { pager })
        })
        .then(resolve)
        .catch(reject)
    })
  },
  deleteOrganizationUser: ({ dispatch }, payload) => {
    const { principalIds, orgId, pager } = payload
    let promises = principalIds.map((user_id) =>
      axios({
        url: `/organizations/${orgId}/users/${user_id}`,
        method: 'DELETE'
      })
    )
    return new Promise((resolve, reject) => {
      Promise.all(promises)
        .then(() => {
          return dispatch('listCurrentOrgUsers', { pager })
        })
        .then(resolve)
        .catch(reject)
    })
  },
  listInvitations: ({ commit, state }) => {
    return new Promise((resolve, reject) => {
      if (!state.activeOrg) {
        return resolve([])
      }
      axios({
        url: `/organizations/${state.activeOrg.id}/invitations`,
        params: { include: 'user' },
        method: 'GET'
      })
        .then(({ data }) => {
          commit('SET_INVITATIONS', data.items)
          let pending = data.items.filter((x) => !x.resolved)
          commit('SET_PENDING_INVITATIONS', pending)
          resolve(data.items)
        })
        .catch(reject)
    })
  },
  resendInvitation: ({ commit, dispatch, state }, invitation) => {
    return new Promise((resolve, reject) => {
      let url = `/organizations/${state.activeOrg.id}/invitations/${invitation.refid}/resend`
      axios({
        url,
        data: {},
        method: 'POST'
      })
        .then(() => {
          return dispatch('listInvitations')
        })
        .then(() => {
          resolve(true)
        })
        .catch(reject)
    })
  },
  cancelInvitation: ({ dispatch }, invitation) => {
    return new Promise((resolve, reject) => {
      let url = `/organizations/${state.activeOrg.id}/invitations/${invitation.refid}/cancel`
      axios({
        url,
        data: {},
        method: 'POST'
      })
        .then(() => {
          return dispatch('listInvitations')
        })
        .then(() => {
          resolve(true)
        })
        .catch(reject)
    })
  },
  uploadAvatar: ({ commit, dispatch }, payload) => {
    const { orgId, data } = payload
    let url = `/organizations/${orgId}/avatar`
    return new Promise((resolve, reject) => {
      axios({
        url,
        data,
        method: 'POST'
      })
        .then(({ data }) => {
          Promise.all([
            dispatch('me/updateOrganizations', data, { root: true }),
            dispatch('app/setContextObj', { ...data, type: 'org' }, { root: true })
          ]).then(() => {
            commit('SET_CURRENT_ORGANIZATION', data)
            resolve(data)
          })
        })
        .catch(reject)
    })
  }
}

const getters = {
  users: (state) => state.users,
  organizations: (state) => state.organizations,
  currentOrg: (state) => state.currentOrg,
  activeOrg: (state) => state.activeOrg,
  getActiveOrg: (state, getters, rootState, rootGetters) => (orgRef) =>
    rootGetters['me/organizations'].find((item) => item.refname === orgRef),
  defaultOrgId: (state) => state.defaultOrgId
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}

export { NAMESPACE, ORG_USERS }
